import { createSlice } from '@reduxjs/toolkit';

import type { AuthState } from '@/types/auth/authTypes';

import {
  loadAuthSession,
  logout,
  requestToken,
  signup,
  updateUserProfile,
  verifyEmail,
  verifyLoginToken,
  verifyToken,
} from './actions';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isLoginInProgress: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestToken.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        if (state.user) {
          state.user = {
            ...state.user,
            nomeCompleto: action.payload.nomeCompleto,
            perfilCompleto: true,
          };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(verifyToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isLoginInProgress = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.isLoginInProgress = false;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isLoginInProgress = false;
      })
      .addCase(verifyLoginToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isLoginInProgress = true;
      })
      .addCase(verifyLoginToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.isLoginInProgress = false;
      })
      .addCase(verifyLoginToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isLoginInProgress = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(loadAuthSession.pending, (state) => {
        if (!state.user && !state.isLoginInProgress) {
          state.isLoading = true;
        }
      })
      .addCase(loadAuthSession.fulfilled, (state, action) => {
        if (!state.isLoginInProgress) {
          state.isLoading = false;
          state.user = action.payload.user;
          state.isAuthenticated = true;
        }
      })
      .addCase(loadAuthSession.rejected, (state) => {
        state.isLoading = false;
        if (!state.user) {
          state.isAuthenticated = false;
        }
      })
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        if ('user' in action.payload && 'token' in action.payload) {
          state.user = action.payload.user;
          state.isAuthenticated = true;
        }
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser } = authSlice.actions;
export default authSlice.reducer;
